/**
 * Filter helper utilities for ObservationMetrics
 * Contains functions for filtering, matching, and processing observation data
 */

/**
 * Helper to read nested properties like 'zone.id' from an item
 */
export const getValueByPath = (obj: any, path: string) => {
  if (!obj || !path) return null;
  return path.split('.').reduce((acc: any, part: string) => {
    if (acc === undefined || acc === null) return null;
    return acc[part];
  }, obj as any);
};

/**
 * Recursive search to find a match anywhere in the item when direct key lookup fails
 */
export const findAnyMatch = (obj: any, filterNormalized: string, depth = 0): boolean => {
  if (obj === undefined || obj === null) return false;
  if (depth > 3) return false; // prevent deep recursion
  if (typeof obj === 'string' || typeof obj === 'number' || typeof obj === 'boolean') {
    return String(obj).toLowerCase().includes(filterNormalized);
  }
  if (Array.isArray(obj)) {
    return obj.some((el) => findAnyMatch(el, filterNormalized, depth + 1));
  }
  if (typeof obj === 'object') {
    // direct id/value equality
    if ('id' in obj && String(obj.id).toLowerCase() === filterNormalized) return true;
    if ('value' in obj && (typeof obj.value === 'string' || typeof obj.value === 'number') && String(obj.value).toLowerCase().includes(filterNormalized)) return true;
    if ('label' in obj && String(obj.label).toLowerCase().includes(filterNormalized)) return true;
    if ('displayText' in obj && String(obj.displayText).toLowerCase().includes(filterNormalized)) return true;

    return Object.values(obj).some((v) => findAnyMatch(v, filterNormalized, depth + 1));
  }
  return false;
};

/**
 * Extract candidate keys from a filter value (handles various data structures)
 */
export const extractCandidateKeysFromValue = (v: any): string[] => {
  if (v === undefined || v === null) return [];
  const out: string[] = [];
  if (typeof v === 'string' || typeof v === 'number') out.push(String(v));
  else if (typeof v === 'object') {
    if (v.id) out.push(String(v.id));
    if (v.email) out.push(String(v.email));
    if (v.displayText) out.push(String(v.displayText));
    if (v.label) out.push(String(v.label));
    if (v.value) {
      if (typeof v.value === 'string' || typeof v.value === 'number') out.push(String(v.value));
      if (typeof v.value === 'object') {
        const inner: any = v.value;
        if (inner.id) out.push(String(inner.id));
        if (inner.email) out.push(String(inner.email));
        if (inner.displayText) out.push(String(inner.displayText));
        if (inner.gpid) out.push(String(inner.gpid));
      }
    }
  }
  return Array.from(new Set(out)).filter(Boolean);
};

/**
 * Get person field candidates from an item based on type (created or finalized)
 */
export const personFieldCandidatesForItem = (item: any, type: 'created' | 'finalized') => {
  const raw = (item as any).rawData ?? item;
  const candidates: string[] = [];
  if (type === 'created') {
    // common fields used for Created By in various endpoints
    candidates.push((item as any).by ?? (item as any).Created_By ?? (item as any).CreatedBy ?? (item as any).createdBy ?? raw?.Created_By ?? raw?.CreatedBy ?? raw?.WALK_STARTED_BY ?? raw?.created ?? raw?.createdBy ?? raw?.CreatedBy);
  } else {
    // finalized / modified
    candidates.push((item as any).modifiedBy ?? (item as any).Modified_By ?? (item as any).ModifiedBy ?? (item as any).finalizedBy ?? raw?.Modified_By ?? raw?.ModifiedBy ?? raw?.RSUsername ?? raw?.Last_Modified_By ?? raw?.LastModifiedBy ?? raw?.FinalizedBy ?? raw?.finalizedBy);
  }
  return Array.from(new Set(candidates)).filter(Boolean) as string[];
};

/**
 * Match person filter exactly against item fields
 */
export const matchPersonFilterExact = (item: any, value: any, type: 'created' | 'finalized') => {
  const candidates = extractCandidateKeysFromValue(value).map((s) => (s === undefined || s === null ? null : String(s).toLowerCase())).filter(Boolean);
  if (candidates.length === 0) return true; // nothing specific to match
  const itemCandidates = personFieldCandidatesForItem(item, type).map((s) => (s === undefined || s === null ? null : String(s).toLowerCase())).filter(Boolean);
  // exact equality check against any of the canonical person fields
  for (const cand of candidates) {
    if (itemCandidates.includes(cand)) return true;
  }
  return false;
};
